import {
    Label,
    NumericField,
    SelectableButtonItem,
    SelectableButtonsList,
    TextField,
    useDeviceSize,
    convertDateToMobile,
    DateField,
    AreaNotification,
    useNotifications,
    LocationOption,
    LocationSearchField,
    useGridStyles,
    Layout,
    SelectField,
    NormalizedPanel,
    NormalizedJobTitleSearchField,
    NormalizedJobTitleOption,
    LocationSearchModeEnum,
} from "@relode/core";
import * as domain from "@relode/core/domain";
import * as React from "react";

import { useAddJobContext } from "../../_infra";
import { useClasses } from "./steps.styling";
import {
    ContractDurationType,
    JobType,
    JobSalaryType,
} from "@relode/core/domain";
import classnames from "classnames";

interface Step2Props {
    handleFieldChange: (field: string) => (e: any) => void;
    handleButtonFieldChange: (
        field: string
    ) => (value: SelectableButtonItem) => void;
}
export const Step2 = (props: Step2Props) => {
    const classes = useClasses({});
    const gridStyles = useGridStyles({});
    const device = useDeviceSize();
    const {
        jobSteps: {
            stepOne: { hasActiveTeamPlan },
            stepTwo: {
                location,
                jobTypeId,
                jobSalaryTypeId,
                contractStartDate,
                normalizedJobName,
                normalizedLocation,
            },
        },
        formikProps,
        setFieldValue,
        state: {
            ui: { isLoading },
        },
    } = useAddJobContext();

    const { actions: notifications } = useNotifications();

    const { handleFieldChange, handleButtonFieldChange } = props;

    const { errors, validateField } = formikProps;

    const jobType = domain.JobType.fromId(jobTypeId);

    React.useEffect(() => {
        if (device === "mobile" && contractStartDate instanceof Date) {
            const contractStart = convertDateToMobile<any>(contractStartDate);
            setFieldValue(contractStart, "stepTwo.contractStartDate");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [device, contractStartDate]);

    React.useEffect(() => {
        if (hasActiveTeamPlan || isLoading)
            return notifications.closeAreaNotification(
                "addJobNoActiveTeamPlanWarning"
            );

        notifications.showArea(
            "warning",
            "Your plan has expired. Please contact your job manager to update your plan.",
            "addJobNoActiveTeamPlanWarning",
            { canClose: false }
        );
    }, [hasActiveTeamPlan, notifications, isLoading]);

    const filterChars = e => {
        if (/[0-9]+/g.test(e.key) === false) {
            e.preventDefault();
        }
    };

    const handleHoursPerWeekChange = e => {
        if (e.target.value)
            setFieldValue(parseInt(e.target.value), "stepTwo.hoursPerWeek");
    };

    const handleJobTypeFieldChange = (field: string) => (
        item: SelectableButtonItem
    ) => {
        setFieldValue(item.id, field);
        if (JobType.isContractOrLocum(item.id)) {
            setFieldValue(JobSalaryType.PerHour.id, "stepTwo.jobSalaryTypeId");
            setFieldValue(new Date(), "stepTwo.contractStartDate");
        }
        validateField("stepTwo.jobSalaryInUSDollars");
    };

    const handleJobSalaryTypeFieldChange = (field: string) => (
        item: SelectableButtonItem
    ) => {
        setFieldValue(item.id, field);
        validateField("stepTwo.jobSalaryInUSDollars");
    };

    const handleLocationChange = (location: LocationOption) => {
        setFieldValue(location, "stepTwo.location");
    };

    const handleNormalizedJobTitleChange = (
        item?: never,
        normalizedJobTitle?: domain.NormalizedJobTitle
    ) => {
        setFieldValue(normalizedJobTitle?.jobName, "stepTwo.normalizedJobName");
    };

    const handleNormalizedLocationChange = (
        item?: never,
        location?: domain.Location
    ) => {
        setFieldValue(location || null, "stepTwo.normalizedLocation");
    };

    const selectedNormalizedJobTitleOption: NormalizedJobTitleOption = !!normalizedJobName
        ? {
              externalId: "",
              jobName: normalizedJobName,
              confidencePercentage: 1.0,
              description: "",
          }
        : null;
    const selectedLocationOption: LocationOption = !!normalizedLocation
        ? {
              locationId: normalizedLocation?.locationId,
              locationName: normalizedLocation?.locationName,
          }
        : null;

    return (
        <div className={classes.form}>
            <AreaNotification areaName="addJobNoActiveTeamPlanWarning" />
            <div className={classes.section}>
                <div
                    className={classnames(
                        gridStyles.spanTwoColumns,
                        gridStyles.twoColumn
                    )}
                >
                    <TextField
                        data-cy="job-name-input"
                        id="jobName"
                        name="stepTwo.jobName"
                        label="job title"
                        className={classes.formField}
                        inputProps={{ maxLength: 100 }}
                        onChange={handleFieldChange("stepTwo.jobName")}
                    />
                    <LocationSearchField
                        id="stepTwo.location"
                        name="stepTwo.location"
                        label="location"
                        onSelect={handleLocationChange}
                        selectedLocation={{
                            locationId: location?.locationId,
                            locationName: location?.locationName,
                        }}
                        mode={LocationSearchModeEnum.CityStatePostalCode}
                    />
                </div>
            </div>
            <div className={classes.section}>
                <NormalizedPanel>
                    <NormalizedJobTitleSearchField
                        id="stepTwo.normalizedJobName"
                        name="stepTwo.normalizedJobName"
                        label="job title"
                        fullWidth
                        selectedNormalizedJobTitle={
                            selectedNormalizedJobTitleOption
                        }
                        onSelect={handleNormalizedJobTitleChange}
                        error={!!errors.stepTwo?.normalizedJobName}
                        helperText={
                            !!errors.stepTwo?.normalizedJobName
                                ? errors.stepTwo.normalizedJobName
                                : null
                        }
                    />
                    <LocationSearchField
                        id="stepTwo.normalizedLocation"
                        name="stepTwo.normalizedLocation"
                        label="location"
                        fullWidth
                        selectedLocation={selectedLocationOption}
                        onSelect={handleNormalizedLocationChange}
                        error={!!errors.stepTwo?.normalizedLocation}
                        mode={LocationSearchModeEnum.CityState}
                    />
                </NormalizedPanel>
            </div>
            <div className={classes.section}>
                <Label label="job type" fullWidth>
                    <SelectableButtonsList
                        id="jobType"
                        className={classes.selectableButtonsList}
                        onClick={handleJobTypeFieldChange("stepTwo.jobTypeId")}
                        items={domain.JobType.asArray().map(jt => ({
                            id: jt.jobTypeId,
                            label: jt.jobTypeName,
                            selected: jt.jobTypeId === jobTypeId,
                        }))}
                        size="lg"
                        error={!!errors.stepTwo?.jobTypeId}
                        helperText={
                            !!errors.stepTwo ? errors.stepTwo.jobTypeId : null
                        }
                    />
                </Label>
            </div>
            <div className={classes.section}>
                <NumericField
                    id="quantity"
                    name="stepTwo.quantity"
                    label="# of positions"
                    type="tel"
                    className={classes.formField}
                    fullWidth
                    onChange={handleFieldChange("quantity")}
                    onKeyPress={filterChars}
                    inputProps={{
                        "data-cy": "job-positions-input",
                        min: 1,
                        max: 100,
                    }}
                />
            </div>

            <div className={classes.section}>
                <Layout mode="25/25/50">
                    <NumericField
                        label="hours per week"
                        id="hoursPerWeek"
                        name="stepTwo.hoursPerWeek"
                        className={classes.formField}
                        onChange={handleHoursPerWeekChange}
                        onKeyPress={filterChars}
                        inputProps={{
                            "data-cy": "job-hoursPerWeek-input",
                        }}
                    />
                    <NumericField
                        label="desired salary"
                        data-cy="jobSalary"
                        id="stepTwo.jobSalaryInUSDollars"
                        name="stepTwo.jobSalaryInUSDollars"
                        startAdornment="dollar"
                        allowDecimal
                    />
                    <div className={classes.jobSalaryTypeButtons}>
                        <SelectableButtonsList
                            id="jobSalaryType"
                            disabled={jobType.isContractOrLocum()}
                            className={classes.selectableButtonsList}
                            onClick={handleJobSalaryTypeFieldChange(
                                "stepTwo.jobSalaryTypeId"
                            )}
                            items={domain.JobSalaryType.asArray().map(jst => ({
                                id: jst.id,
                                label: jst.name,
                                selected: jst.id === jobSalaryTypeId,
                            }))}
                            size="md"
                            error={!!errors.stepTwo?.jobSalaryTypeId}
                            helperText={
                                !!errors.stepTwo
                                    ? errors.stepTwo.jobSalaryTypeId
                                    : null
                            }
                        />
                    </div>
                </Layout>
            </div>

            {jobType.isContractOrLocum() ? (
                <div className={classes.section}>
                    <Layout mode="50/25/25">
                        <DateField
                            data-cy={"contractStartDate-control"}
                            value={contractStartDate}
                            label="start date"
                            name="stepTwo.contractStartDate"
                            error={!!errors.stepTwo?.contractStartDate}
                        />
                        <TextField
                            id="contractDuration"
                            name="stepTwo.contractDuration"
                            label="duration"
                            type="tel"
                            onChange={handleFieldChange(
                                "stepTwo.contractDuration"
                            )}
                            InputLabelProps={{
                                shrink: true,
                            }}
                            onKeyPress={filterChars}
                            inputProps={{
                                min: 0,
                            }}
                        />
                        <SelectField
                            mode="list"
                            className={classes.removeMuiMargin}
                            label="units"
                            id="stepTwo.contractDurationTypeId"
                            name="stepTwo.contractDurationTypeId"
                            options={ContractDurationType.asArray()}
                            getOptionLabel={o => o.contractDurationTypeName}
                            setFieldValue={o => o.contractDurationTypeId}
                            getFieldOption={(o, v) =>
                                o.contractDurationTypeId === v
                            }
                        />
                    </Layout>
                </div>
            ) : null}
        </div>
    );
};
